<template>
  <div class="template-preview-page">
    <header class="template-preview-header">
      <div class="template-preview-header-inner">
        <builderall-icon code='builderall-logo' class="template-preview-logo"></builderall-icon>
        <div class="template-preview-toolbar-center">
          <button type="button" class="template-preview-device"
            :class="{ 'template-preview-device-active': device === 'desktop' }" @click="changeDevice('desktop')">
            <i class=" ct-icon-desktop"></i>
          </button>
          <div class="template-preview-toolbar-divider" aria-hidden="true" />
          <button type="button" class="template-preview-device"
            :class="{ 'template-preview-device-active': device === 'mobile' }" @click="changeDevice('mobile')">
            <i class=" ct-icon-mobile"></i>
          </button>
        </div>
        <div class="template-preview-header-actions">
          <button type="button" class="template-preview-btn-edit" @click="openModal()">
            {{ $t('ct-edit') }}
          </button>
          <a
            class="template-preview-btn-browse"
            :href="browseTemplatesUrl"
            :target="isLoggedIn ? null : '_blank'"
            :rel="isLoggedIn ? null : 'noopener noreferrer'"
          >
            {{ $t('ct-browse-all-templates') }}
          </a>
        </div>
      </div>
    </header>

    <main class="template-preview-main" :class="`template-preview-main-${device}`">
      <div class="template-preview-canvas">
        <img src="@/assets/images/phone-frame.png" class="template-preview-phone-frame" />
        <iframe ref="templateIframe" :src="iframeUrl" height="100%" width="100%" frameborder="0"></iframe>
      </div>
    </main>

    <sweet-modal ref="templateModal" width="660px" blocking class="template-preview-sweet-modal">
      <div class="template-preview-modal">
        <h2 class="template-preview-modal-title">{{ $t('ct-start-with-this-template') }}</h2>
        <div :style="{ backgroundImage: `url(${template.thumbnail_portrait})` }" class="template-preview-modal-thumb">
        </div>
        <p class="template-preview-modal-sub">{{ $t('ct-you-can-customize-everything-later') }}</p>
        <button type="button" class="template-preview-modal-cta" @click="$refs.templateModal.close(); openAIWizard()">
          {{ $t('ct-start-building') }}
        </button>
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';
import { get } from '@/js/services/editor/template';
import { transferDuplicateTemplate } from '@/js/services/editor/website';

const OPEN_AFTER_MS = 5000;

export default {
  name: 'TemplatePreview',

  components: {
    SweetModal
  },

  data() {
    return {
      templateSlug: this.$route.params.template_slug,
      template: {
        thumbnail_portrait: null,
      },
      device: 'desktop',

      modalOpened: false,
      delayedOpenTimer: null,
    };
  },

  mounted() {
    const vm = this;
    // show popup when user tries to leave the page
    document.addEventListener('mouseout', (e) => {
      if (e.clientY <= 0 && !vm.modalOpened) {
        vm.openModal();
      }
    });

    // show popup after 5 seconds of iframe loading
    this.$refs.templateIframe.addEventListener('load', () => {
      console.log('iframe loaded');
      vm.delayedOpenTimer = setTimeout(() => {
        if (!vm.modalOpened) {
          vm.openModal();
        }
        clearTimeout(vm.delayedOpenTimer);
        vm.delayedOpenTimer = null;
      }, OPEN_AFTER_MS);
    });

    get(this.templateId).then(response => {
      if (response.data.template)
        this.template = response.data.template;
    });

    document.addEventListener("ai-wizard-skip", function (event) {
      vm.saveWebsite();
    });
  },

  computed: {
    iframeUrl() {
      return 'https://' + this.templateSlug + '.template.builderall.com' + '?version=' + this.version;
    },
    templateId() {
      return this.templateSlug.replace('cheetah-template-', '').replace('cheetah-premium-template-', '');
    },
    isLoggedIn() {
      return !!(localStorage.getItem('login') === 'true' && localStorage.getItem('token'));
    },
    browseTemplatesUrl() {
      return this.isLoggedIn ? '/#/create-website?step=template' : 'https://builderall.com/templates';
    }
  },

  methods: {
    changeDevice(device) {
      this.device = device;
    },

    openModal() {
      this.modalOpened = true;
      this.$refs.templateModal.open();
    },

    closeModal() {
      if (this.$refs.templateModal) {
        this.$refs.templateModal.close();
      }
    },

    closeAIWizard() {
      document.removeEventListener("ai-wizard-skip", this.saveWebsite);
      const wizardElement = document.querySelector("builderall-ai-wizard");
      if (wizardElement) {
        wizardElement.remove();
      }
    },

    openAIWizard() {
      const searchParams = new URLSearchParams(window.location.search);
      const from = searchParams.get("from");
      const prompt = searchParams.get("prompt");
      const el = document.createElement("builderall-ai-wizard");
      el.setAttribute("show", "true");
      el.setAttribute("allowclose", from == 'ba-home' ? "true" : "false");
      el.setAttribute("allowskip", from == 'editor' ? "true" : "false");
      el.setAttribute("templateid", this.templateId);
      el.setAttribute("description", decodeURIComponent(prompt) || "");
      document.body.appendChild(el);
    },

    saveWebsite() {
      this.$loading.show();
      transferDuplicateTemplate(
        { encrypted_id: this.template.encrypted_id },
        {
          title: this.$t("ct-my-website-title"),
          description: this.$t("ct-my-website-description"),
          type: "template",
          template_flag: "",
          is_premium: this.template.is_premium,
          original_template_id: this.template.id,
          amount: this.template.amount,
          group: 0,
        }
      )
        .then(({ data }) => {
          if (data.success === false) {
            this.$snotify.warning(this.$t(data.error));
          } else {
            let home = data.website.layouts[0].pages.find(page => !!page.initial);
            let pageHome = home || data.website.layouts[0].pages[0];

            this.closeAIWizard();

            this.$router.push({
              name: 'editor',
              params: { page_id: pageHome.encrypted_id }
            });
          }
        })
        .finally(() => (
          this.$loading.hide()
        ));
    },
  }
};
</script>

<style lang="scss" scoped>
.template-preview-page {
  min-height: 100vh;
  background: #e4eaf6;
  font-family: 'Montserrat', 'Mulish', sans-serif;
}

.template-preview-header {
  height: 70px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
  position: sticky;
  top: 0;
  z-index: 10;
}

.template-preview-header-inner {
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.template-preview-logo {
  display: block;
  --builderall-icon-size: 59px;
  --builderall-icon-color: #0080FC;
}

.template-preview-toolbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    display: none;
  }
}

.template-preview-device {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 0;
  width: 45px;
  height: 50px;
  color: #d8d8d8;

  i {
    font-size: 30px;
  }

  &-active {
    background: #0072e1;
    color: #fff;
  }
}

.template-preview-monitor-stand {
  margin-top: 0;
  display: block;
}

.template-preview-toolbar-divider {
  width: 1px;
  height: 35px;
  background: #d8d8d8;
}

.template-preview-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.template-preview-btn-edit {
  border: none;
  border-radius: 6px;
  background: #0072e1;
  color: #fff;
  font-family: inherit;
  font-weight: 700;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 20px;
  cursor: pointer;
  text-transform: uppercase;
}

.template-preview-btn-browse {
  border: 1px solid #0072e1;
  border-radius: 6px;
  background: #fff;
  color: #0072e1;
  font-family: inherit;
  font-weight: 700;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 20px;
  cursor: pointer;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
}

.template-preview-phone-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.template-preview-main {
  &-mobile {
    width: 374px;
    margin: 2vh auto;
    height: 760px;
    border-radius: 12px;

    iframe {
      padding: 8px;
      border-radius: 64px;
    }
  }

  &-desktop {
    width: 100%;
    height: calc(100vh - 70px);
    border-radius: 0;

    .template-preview-phone-frame {
      display: none;
    }
  }
}

.template-preview-canvas {
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  height: 100%;
  margin-left: auto;
  margin-right: auto;

  iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.template-preview-hero-img {
  display: block;
  width: 100%;
  height: auto;
}
</style>

<style lang="scss">
/* sweet-modal root is teleported — keep unscoped overrides minimal */
.template-preview-sweet-modal {
  .sweet-modal {
    border-radius: 20px;
    border: 1px solid #e9e9e9;
    box-shadow: 14px 10px 33px -1px rgba(0, 0, 0, 0.15);
    padding: 0;
    overflow: visible;
  }

  .sweet-content {
    padding: 0;
  }
}

.template-preview-modal {
  position: relative;
  padding: 28px 20px 20px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
  }
}

.template-preview-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  line-height: 0;
}

.template-preview-modal-title {
  margin: 0 0 24px;
  font-family: 'Mulish', sans-serif;
  font-weight: 700;
  font-size: 36px;
  line-height: 1.1;
  color: #050505;
}

.template-preview-modal-thumb {
  display: block;
  width: 280px;
  height: 365px;
  border: 5px solid #ccc;
  box-shadow: 0 0 6.2px rgba(0, 0, 0, 0.25);
  margin: 0 auto 24px;
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.template-preview-modal-sub {
  margin: 0 0 28px;
  font-family: 'Mulish', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 1.1;
  color: #050505;
}

.template-preview-modal-cta {
  border: none;
  border-radius: 14px;
  background: #0072e1;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.1;
  text-transform: uppercase;
  padding: 12px 42px;
  min-width: 295px;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/external/TemplatePreview.vue